/**
 * The durable outbox store.
 *
 * Specification: ../test/sqlite-store.spec.ts. Master plan §3 K4, §2 D6.
 *
 * `MemoryOutboxStore` is a test double. This is the money ledger, which the
 * plan puts on wa-sqlite over OPFS with `PRAGMA synchronous = FULL`.
 *
 * That pragma is the point, and it is worth being explicit about what it buys.
 * `NORMAL` means the operating system acknowledged the write. `FULL` means the
 * storage device did. Between the two sits every power cut, forced restart and
 * battery-pull that happens in a restaurant, and on the money path the gap is
 * an evening of takings the till believes it recorded and did not. It costs
 * write throughput that a POS does not need.
 *
 * Written against a small driver port rather than against bun:sqlite, because
 * the SAME SQL must run in three places: `bun test` and the venue hub on
 * bun:sqlite, and the POS on wa-sqlite over OPFS. A second implementation of
 * this logic would diverge, and the divergence would only ever surface after
 * a crash — which is the worst possible time to discover it.
 */

import { decodePayload, encodePayload } from "@gf/kernel/json";
import type { SqliteDriver } from "@gf/kernel/sqlite";
import type { OutboundEvent, OutboxRecord, OutboxStore, RecordOrigin, RecordState } from "./outbox";

export type { SqliteDriver } from "@gf/kernel/sqlite";
export { decodePayload, encodePayload } from "@gf/kernel/json";

interface Row {
  event_id: string;
  seq: number;
  event_json: string;
  enqueued_at: number;
  attempts: number;
  last_error: string | null;
  state: string;
  origin: string;
}

/**
 * Rebuild a record.
 *
 * The payload is decoded rather than merely parsed, so `bigint` money comes
 * back as `bigint`. A store that returned Numbers here would be a float on the
 * money path that nobody had written down as one — and it would only diverge
 * for amounts large enough to matter.
 */
function toRecord(row: Row): OutboxRecord {
  const parsed = decodePayload<Omit<OutboundEvent, "occurredAt"> & { occurredAt: string }>(
    row.event_json,
  );
  return {
    eventId: row.event_id,
    seq: row.seq,
    event: { ...parsed, occurredAt: new Date(parsed.occurredAt) },
    enqueuedAt: row.enqueued_at,
    attempts: row.attempts,
    lastError: row.last_error,
    state: row.state as RecordState,
    origin: row.origin as RecordOrigin,
  };
}

export class SqliteOutboxStore implements OutboxStore {
  readonly #driver: SqliteDriver;

  constructor(driver: SqliteDriver) {
    this.#driver = driver;
  }

  /** Idempotent: every app start runs it. */
  async migrate(): Promise<void> {
    await this.#driver.exec(`
      CREATE TABLE IF NOT EXISTS outbox (
        event_id    TEXT    PRIMARY KEY,
        seq         INTEGER NOT NULL,
        event_json  TEXT    NOT NULL,
        enqueued_at INTEGER NOT NULL,
        attempts    INTEGER NOT NULL DEFAULT 0,
        last_error  TEXT,
        state       TEXT    NOT NULL DEFAULT 'pending',
        origin      TEXT    NOT NULL DEFAULT 'local',
        CONSTRAINT outbox_state_known CHECK (state IN ('pending','quarantined')),
        CONSTRAINT outbox_origin_known CHECK (origin IN ('local','peer'))
      )
    `);
    // The claim path: pending work, oldest first. The FIFO IS this index.
    await this.#driver.exec(
      "CREATE INDEX IF NOT EXISTS outbox_pending_idx ON outbox (state, seq)",
    );
  }

  async append(record: OutboxRecord): Promise<void> {
    // The PRIMARY KEY makes a duplicate impossible rather than unlikely, and
    // it holds even if a future caller reaches the store directly instead of
    // going through Outbox.enqueue.
    await this.#driver.run(
      `INSERT INTO outbox
         (event_id, seq, event_json, enqueued_at, attempts, last_error, state, origin)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT (event_id) DO NOTHING`,
      [
        record.eventId,
        record.seq,
        encodePayload({ ...record.event, occurredAt: record.event.occurredAt.toISOString() }),
        record.enqueuedAt,
        record.attempts,
        record.lastError,
        record.state,
        record.origin,
      ],
    );
  }

  async has(eventId: string): Promise<boolean> {
    const rows = await this.#driver.all<{ n: number }>(
      "SELECT 1 AS n FROM outbox WHERE event_id = ? LIMIT 1",
      [eventId],
    );
    return rows.length > 0;
  }

  async head(limit: number): Promise<OutboxRecord[]> {
    const rows = await this.#driver.all<Row>(
      "SELECT * FROM outbox WHERE state = 'pending' ORDER BY seq LIMIT ?",
      [limit],
    );
    return rows.map(toRecord);
  }

  async remove(eventIds: readonly string[]): Promise<void> {
    if (eventIds.length === 0) return;
    const placeholders = eventIds.map(() => "?").join(",");
    await this.#driver.run(`DELETE FROM outbox WHERE event_id IN (${placeholders})`, eventIds);
  }

  async update(records: readonly OutboxRecord[]): Promise<void> {
    for (const record of records) {
      await this.#driver.run(
        "UPDATE outbox SET attempts = ?, last_error = ?, state = ? WHERE event_id = ?",
        [record.attempts, record.lastError, record.state, record.eventId],
      );
    }
  }

  async all(): Promise<OutboxRecord[]> {
    const rows = await this.#driver.all<Row>("SELECT * FROM outbox ORDER BY seq", []);
    return rows.map(toRecord);
  }

  /**
   * The next local sequence number.
   *
   * Read from the table rather than from an in-memory counter, so a restarted
   * till continues where it left off. A sequence that restarted at zero would
   * interleave tonight's events with the ones still queued, and the FIFO would
   * stop being one.
   */
  async nextSeq(): Promise<number> {
    const rows = await this.#driver.all<{ highest: number | null }>(
      "SELECT MAX(seq) AS highest FROM outbox",
      [],
    );
    return (rows[0]?.highest ?? 0) + 1;
  }
}
